import { StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { getCurrentBrand } from '@/utils/getCurrentBrand';
import { log } from 'lib/data/compute/ads/helpers';

import { getSite } from '@/utils/getSite';
import { getOcelotConfig } from 'lib/configs/ocelot';
import { AdsMeta, Meta } from 'lib/labrador/types';
import Head from 'next/head';
import Script from 'next/script';
import { isUndefined } from 'typesafe-utils';

export interface StandaloneAdTemplateProps extends StandaloneComponentProps {
  pageMeta?: Meta;
  adsData?: AdsMeta;
}

export const StandaloneAdTemplate: StandaloneComponent<StandaloneAdTemplateProps> = ({
  pageMeta = {},
  adsData = {},
}) => {
  const { isNativeAd, hideAds, explicitContent, device, firstVideoAutoStart } = pageMeta;
  const showAds = !(isNativeAd || hideAds);
  if (!showAds) {
    return null;
  }

  const site = getSite();

  const { adsPreConfig: preConfig = {}, ...slimAdsData } = adsData;
  const enableDemandManager = `${isUndefined(preConfig?.enableDemandManager) || preConfig?.enableDemandManager === 'true'}`;
  const enableAdnami = `${preConfig?.enableAdnami === 'true'}`;

  const adsBundleVersion = preConfig?.siteAdsBundleVersions?.bundleVersion || '';
  const adsBundlePath = preConfig?.adsBundleUrl;

  log('adsBundlePath', adsBundlePath);

  const variant = '07';
  const cacheBuster = `v=${adsBundleVersion}-${variant}`;

  const { host } = getCurrentBrand();
  const seenthisPath = 'https://cdn.allermedia.se/seenthis/high-impact.min.js';
  const brandStoriesAdScript = 'https://cdn.sitestream.co/libs/storylines/11.x.x/storylines-bridge.js';
  const adnamiPath = 'https://macro.adnami.io/macro/spec/adsm.macro.747590aa-4758-4281-839e-4b9b65def16a.js';
  const gptPath = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
  const adsManagerUrl = `${adsBundlePath}?${cacheBuster}`;
  const adnamiConfPath = `/common/js/ads/adnami-conf.js`;

  const adsUniqueId = adsData.adsUniqueId || '';
  const topAdTarget = `top_ad-${adsUniqueId}`;

  const preload = getOcelotConfig('ads.preload.enabled');
  const adsConfObj = {
    pageMeta: { isNativeAd, hideAds, explicitContent, device, firstVideoAutoStart },
    adsData: { ...slimAdsData },
    hostName: host,
    topAdTarget,
    loadedByAdsLib: { demandManager: true },
  };

  const adsConf: string = JSON.stringify(adsConfObj);

  return (
    <>
      <Head>
        {/* Always preload prebid since it loads async from ads-bundle*/}
        {enableDemandManager && (
          <link
            rel="preload"
            href="https://micro.rubiconproject.com/prebid/dynamic/13012.js"
            as="script"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        )}
        {preload && (
          <>
            {enableAdnami && (
              <>
                <link rel="preload" href={adnamiConfPath} as="script" />
                <link rel="preload" href={adnamiPath} as="script" />
                <link rel="preload" href={seenthisPath} as="script" />
                <link rel="preload" href={brandStoriesAdScript} as="script" />
              </>
            )}
            <link rel="preload" href={gptPath} as="script" />
            <link
              rel="preload"
              href={adsManagerUrl}
              as="script"
              {...{ crossOrigin: site.includes('motherhood') ? 'anonymous' : undefined }}
            />
          </>
        )}
      </Head>

      {/* Top_ad - High Impact Formats for adnami and seenthis*/}
      {enableAdnami && (
        <>
          <Script id="adnami-ads-conf" src={adnamiConfPath} />
          <Script id="adnami-ads-formats" src={adnamiPath} />
          <Script id="seenthis" src={seenthisPath} />
          <Script id="brand-stories-ads" src={brandStoriesAdScript} />
        </>
      )}

      {/* GoogleAdsManager */}
      <Script id="gpt-initiator" src={`${gptPath}`} />

      {/* Prebid loadedByAdsLib */}

      {/* AdsLibrary */}
      <Script
        id="ads-manager"
        src={adsManagerUrl}
        {...{ 'data-ads-conf': adsConf }}
        {...{ crossOrigin: site.includes('motherhood') ? 'anonymous' : undefined }}
      />
    </>
  );
};
